import type { PaywallGate } from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import type { Plan } from '@parcel/types';
import { Badge } from '@parcel/ui';
import { Lock } from 'lucide-react';
import { useCaptureOnce } from '../../lib/analytics.js';
import { useEntitlements } from '../../lib/entitlements.js';
import { UpgradeButton, planLabel } from './upgrade-button.js';

export function LockedFeaturePanel({
  feature,
  gate,
  requiredPlan,
  benefit,
  workspaceSlug,
  comingSoon = false,
  ctaLabel,
}: {
  feature: string;
  /** Stable analytics slug; `feature` stays the human-readable label. */
  gate: PaywallGate;
  requiredPlan: Plan;
  benefit?: string;
  workspaceSlug: string;
  comingSoon?: boolean;
  /** Overrides the upgrade button label (defaults to "Upgrade"); e.g. "Upgrade to Pro" for API access. */
  ctaLabel?: string;
}) {
  const { effectivePlan, isLoading } = useEntitlements(workspaceSlug);
  // Fire only once entitlements have resolved. useEntitlements fails closed to
  // 'free' while loading and the api-keys render condition is true on that
  // default, so an unguarded effect logs a false paywall_viewed with
  // current_plan 'free' for every paid workspace on a cold load of the page.
  useCaptureOnce(isLoading ? null : gate, () =>
    capture('paywall_viewed', {
      gate,
      required_plan: requiredPlan,
      current_plan: effectivePlan,
    }),
  );

  return (
    <div className="rounded-lg border px-6 py-10 text-center">
      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
        <Lock className="h-5 w-5 text-brand" aria-hidden />
      </div>
      <div className="mt-3 flex items-center justify-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">{feature}</h3>
        <Badge variant="brand">{planLabel(requiredPlan)}</Badge>
      </div>
      {benefit ? (
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{benefit}</p>
      ) : null}
      <div className="mt-4 flex justify-center">
        {comingSoon ? (
          <span className="text-xs text-muted-foreground">Coming soon</span>
        ) : (
          <UpgradeButton workspaceSlug={workspaceSlug} gate={gate}>
            {ctaLabel}
          </UpgradeButton>
        )}
      </div>
    </div>
  );
}
